import type { CrisisNotice } from '../components/CrisisNotice.js';
import type { UpdateMoodCheckin, AdherenceCorrelation, ChangeShareLevel, CbtTool, CognitiveDistortion, CreateMedication, CreateMoodCheckinInput, CreateThoughtRecord, DayAdherence, DayGlance, DoseStatus, Homework, HomeworkAnswer, HomeworkField, HomeworkValue, DoseUnit, LogDose, Medication, MedicationEffect, MedicationReference, MoodCheckin, PublicUser, ServerCapabilities, Share, ShareAccessLogEntry, SharedDailyStatus, SharedView, StreakState, ThoughtRecord, ThoughtRecordDraft, UpdateMedication, VoiceLog, WeekDayState, WeeklyReport, DaySleep, DoorProposalResponse, DoorFileBody, DoorPickKind, Wave, CreateShareInput } from '@adhd/shared';

/**
 * API client.
 *
 * Session lives in an httpOnly cookie, so `credentials: 'include'` is the only
 * auth plumbing needed — there is no token for client JS to hold, and
 * therefore none for an XSS to steal.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The server is running but the feature was never configured (no API key). */
  get isNotConfigured(): boolean {
    return this.code === 'not_configured';
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  /**
   * A genuine network failure — the request never reached the server. Distinct
   * from a 401, which means it arrived and was rejected. Conflating the two
   * produces "can't reach the server" when the truth is "please sign in
   * again", which sends the user off debugging their wifi.
   */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

/**
 * Fired when any request comes back 401. A session can expire, or the server's
 * database can be swapped underneath a page that is already open — in both
 * cases the app is holding a user object that is no longer real, and every
 * subsequent action fails in a confusing way. The session provider listens for
 * this and returns the app to sign-in.
 */
export const UNAUTHORIZED_EVENT = 'adhd:unauthorized';

/**
 * The one route in the app that is meant to be opened by someone who is not
 * signed in and never will be. Kept as a constant because the request helper
 * has to recognise it, and a copy of the string in two places is exactly the
 * kind of thing that drifts apart later.
 */
const PUBLIC_SHARE_PATH = '/shared/';

/**
 * This device's IANA zone, read once per page load.
 *
 * Not cached across loads: travelling and reopening the app should be enough to
 * be in the new zone, without anything to clear.
 */
const deviceZone: string | null = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
})();

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        // Only when there IS a body. Sending `Content-Type: application/json`
        // on a bodyless request makes Fastify reject it with 400
        // FST_ERR_CTP_EMPTY_JSON_BODY before it reaches any route — so every
        // DELETE in this app silently failed: journal entries, thought records,
        // passkeys, medications, shares. The confirm dialog appeared, the
        // request 400'd, the handler swallowed it, and the screen navigated
        // away as though it had worked.
        //
        // This exact fault was found in the smoke suite's curl helper and fixed
        // there. The client was never checked, so the test harness was repaired
        // and the application left broken.
        ...(init.body !== undefined && !(init.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        /*
         * Which day it is, decided where this device is standing.
         *
         * Every "what day was that?" on the server was answered from the single
         * fixed timezone on the profile, so a week in Hawaii filed evening doses
         * against the next New York day — and since the 4am carry-over keeps
         * yesterday's slots reachable on purpose, both days accepted the same
         * dose and the record split in two.
         *
         * Sent on every request rather than on writes alone, because "today" is
         * a question reads ask too: which slots are due, which day the fold is
         * showing. The server validates it and uses it for that request only.
         */
        ...(deviceZone ? { 'X-Client-Timezone': deviceZone } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch {
    // Offline, or the server is down. Say so plainly rather than showing a
    // stack trace to someone who is already having a hard time.
    throw new ApiError(0, 'offline', "Can't reach the server right now. Check your connection.");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (body as { error?: { code: string; message: string; details?: unknown } })?.error;

    // Signing in with the wrong password is also a 401, but that is the user
    // being told "no" on a form they are already looking at — not a stale
    // session. Bouncing them anywhere would be wrong, so auth routes opt out.
    //
    // The shared view opts out for a different reason, and one that is not
    // visible from here: the person on that page is a therapist or a partner
    // holding a link, with no account and no session to go stale. A 401 there
    // means the link itself is closed — revoked, expired, or set to require an
    // account — so bouncing them would drop a stranger onto the sign-in screen
    // of an app they cannot sign in to, in place of the sentence explaining
    // what happened to the link.
    if (res.status === 401 && !path.startsWith('/auth/') && !path.startsWith(PUBLIC_SHARE_PATH)) {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }

    throw new ApiError(
      res.status,
      err?.code ?? 'unknown',
      err?.message ?? 'Something went wrong.',
      err?.details,
    );
  }
  return body as T;
}

/**
 * One of a week's two share-link readings, as the owner's Week page shows it:
 * How the week seemed (How I seem links) or What the week held (What my days
 * held and My worksheets too).
 */
export interface WeekReading {
  kind: 'seemed' | 'held';
  status: 'ready' | 'thin' | 'refused' | 'failed' | string;
  reading: string | null;
  withdrawn: boolean;
  days: WeekDayState[];
  /** Where a written reading goes: on a link, on none, or held back by the door (a life event's name; "ongoing" on links without Life events). */
  onLinks: 'shown' | 'no_link' | 'label' | 'current' | 'ongoing' | null;
}

const get = <T>(path: string): Promise<T> => request<T>(path);
const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = (path: string): Promise<void> => request<void>(path, { method: 'DELETE' });

// --- Types the client needs that aren't in shared -------------------------

/** GET /insights/months/:month. Mirrors `MonthFigures` and `MonthReport` in services/months.ts. */
export interface MonthPage {
  figures: {
    month: string;
    from: string;
    to: string;
    finished: boolean;
    days: number;
    rated: number;
    entries: number;
    worksheets: number;
    worksheetsFinished: number;
    helpNow: number;
    floor: number | null;
    floorDay: string | null;
    ceiling: number | null;
    ceilingDay: string | null;
    doses: { taken: number; scheduled: number } | null;
    exerciseDays: number;
    strip: { day: string; end: number | null; empty: boolean; wrote: boolean }[];
    threads: { label: string; kind: 'event' | 'thread'; startedOn: string; endedOn: string | null; countFrom: string | null }[];
    words: { day: string; end: number; text: string; source: 'entry' | 'note' }[];
  };
  report: { status: 'pending' | 'ready' | 'failed' | 'no_data'; generatedAt: string | null; summaryMd: string | null; error: string | null } | null;
}

export interface TodayMood {
  day: string;
  items: MoodCheckin[];
}

export interface SosStart {
  sosId: string;
  tools: CbtTool[];
  crisis: {
    region: string;
    resources: { name: string; contact: string; description: string; available: string }[];
    emergencyNote: string;
  };
}

export interface DailyQuote {
  id: string;
  text: string;
  author: string;
  sourceNote: string | null;
  isFavorite: boolean;
}

export interface Passkey {
  id: string;
  label: string | null;
  deviceType: string | null;
  /** True for a synced passkey; false means it only exists on one device. */
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface CrisisInfo {
  region: string;
  resources: { name: string; contact: string; description: string; available: string }[];
  emergencyNote: string;
}

/**
 * One expected dose today, with whatever has been logged against it.
 *
 * Written against the handler rather than imported, because `/medications/today`
 * assembles this shape inline and shared has no interface for it. The same is
 * true of [[AdherenceSummary]] below.
 */
export interface DoseSlot {
  medicationId: string;
  medicationName: string;
  doseAmount: number | null;
  doseUnit: DoseUnit;
  /** Local 24h `HH:MM`, straight from the medication's schedule. */
  time: string;
  label: string | null;
  /**
   * Local wall-clock, no timezone — and the slot's identity as well as its
   * time. Hand it back to `logDose` exactly as it arrived: the server matches a
   * dose to its slot on this string, so a reformatted one records a second,
   * unscheduled dose instead of filling the slot the user tapped.
   */
  scheduledFor: string;
  /** null until something has been logged against this slot today. */
  status: DoseStatus | null;
  takenAt: string | null;
  note: string | null;
  /** How it landed, if anything was said. Empty is the normal case. */
  feelings: string[];
  /**
   * Nothing was recorded here and the time has gone, so it is taken as far as
   * anything that counts is concerned.
   *
   * Doses are assumed taken unless something says otherwise, and every ratio the
   * app reports already includes this. No dose row exists for it — the stored
   * record stays the things the user actually said — which is why it sits beside
   * `status` rather than being a value of it. Tapping Taken confirms it and
   * writes the row for real; tapping Missed or Skipped overrides it.
   */
  assumed: boolean;
}

/** A dose logged outside the schedule — as-needed, or off-plan. */
export interface UnscheduledDose {
  medicationId: string;
  medicationName: string;
  status: DoseStatus;
  takenAt: string | null;
  note: string | null;
}

export interface MedicationsToday {
  /** The user's own calendar day, resolved server-side in their timezone. */
  day: string;
  slots: DoseSlot[];
  unscheduled: UnscheduledDose[];
  /**
   * Last night's doses that were never answered, for a few hours after midnight.
   *
   * A dose due at 11pm used to be reachable for an hour and then gone: the
   * screen rebuilt around a new day and took the slot with it. Nothing was lost
   * from the record — it is assumed taken and counted either way — but there was
   * nowhere left to confirm it, or to say it had actually been missed, which is
   * the correction that matters.
   *
   * Empty after 4am, matching the grace a dose gets before it is assumed at all.
   */
  carryOver: DoseSlot[];
}

/** The daily counts and the mood comparison arrive in one response. */
export interface AdherenceSummary {
  from: string;
  to: string;
  byDay: DayAdherence[];
  correlations: AdherenceCorrelation[];
}


/**
 * A life event as its owner sees it — with the id needed to change it, which
 * SharedLifeEvent deliberately lacks because a reader has nothing to change.
 *
 * `label` is '' for an unnamed one, which is the ordinary case rather than a
 * missing value: marking something takes two taps and no keyboard, and naming
 * it is a later step that may never happen.
 */
/** A day's morning and night answers (release 3 of 2.0). */
export interface DayFace {
  day: string;
  sleep: DaySleep | null;
  /** Where the word came from: his tap, or the Shortcut that reads Health (14 Oct 2026). */
  sleepSource: 'you' | 'health' | null;
  /** Set while the day is closed; null if never closed or reopened. */
  closedAt: string | null;
  reopenedAt: string | null;
}

/** Someone in the record. Owner-only; see services/people.ts on the server. */
export interface Person {
  id: string;
  name: string;
  aliases: string[];
  hidden: boolean;
  /** Days they were filed on. */
  days: number;
  lastDay: string | null;
  createdAt: string;
}
export interface DayPerson {
  personId: string;
  name: string;
  /** 'app' = the summariser's pick, dashed; 'you' = added by hand. */
  source: 'app' | 'you';
  voiceLogId: string | null;
}
export interface PersonDay {
  day: string;
  source: 'app' | 'you';
  /** Where the day ended, never a mean. */
  lastMood: number | null;
  checkins: number;
  voiceLogId: string | null;
  title: string | null;
}

export interface OwnLifeEvent {
  id: string;
  label: string;
  /** When it was last asked about, so the reminder can leave it alone. */
  lastCheckedAt: string | null;
  /**
   * The person's own sentence, copied verbatim from the entry that raised this
   * and verified character-for-character on the server.
   *
   * Shown ONLY inside an opened day fold and in the naming editor. Never on
   * Today, never in the Log, never on a share link — SharedLifeEvent has no
   * field for it, so that last one is a compile error rather than a rule — and
   * never in a prompt. The model-written daily line IS served to a link with
   * the daily_status scope, and a past day's read is served forever, so a
   * sentence that entered that prompt could come back reworded in a permanent
   * line a clinician reads.
   */
  quote: string | null;
  startedOn: string;
  endedOn: string | null;
  status: 'active' | 'ended';
  /**
   * Release 2 of 2.0: an event is the big thing; a thread is allowed to be
   * ordinary (a race, a return, a person). Same shape, same rule that the app
   * never names one. `countFrom` is the date his own count runs from when it
   * is not the start; null means startedOn.
   */
  kind: 'event' | 'thread';
  countFrom: string | null;
  /**
   * The journal entry that raised this, when one did.
   *
   * The server has always sent it (life-events.ts) and this interface did not
   * declare it, so the naming editor hedged — "the words are probably in what
   * you wrote that day" — about an entry the app could name exactly.
   */
  voiceLogId: string | null;
}

/**
 * What the App tab shows.
 *
 * Three separate questions, kept apart on purpose: what is CONFIGURED, what is
 * IN FORCE for this account, and whether a real call has actually WORKED.
 * `capabilities` above answers only the first and reports `reasoning: true`
 * whenever a key string exists — which is true of a server running on a revoked
 * key, and was measured to be.
 */
export interface Diagnostics {
  inForce: { aiLevel: string; warmth: string; voice: string; timezone: string };
  configured: {
    reasoning: boolean;
    reasoningProvider: string;
    reasoningModel: string;
    transcription: boolean;
    transcriptionProvider: string;
    transcriptionModel: string;
    companion: boolean;
    sharing: boolean;
    encryptionAtRest: boolean;
    crisisRegion: string;
    signupAllowed: boolean;
    version: string;
  };
  /** Null until a model has been asked for anything since the server started. */
  lastCall: {
    ok: boolean;
    at: string;
    ms: number;
    model: string;
    provider: string;
    error: string | null;
  } | null;
  counts: {
    checkins: number;
    journalEntries: number;
    thoughtRecords: number;
    thoughtRecordsCompleted: number;
    doses: number;
    medications: number;
    sosEvents: number;
    lifeEvents: number;
    weeklyReports: number;
    activeShares: number;
    daysLogged: number;
    firstDay: string | null;
  };
  storage: { databaseBytes: number; audioBytes: number };
}

export interface AiCheck {
  ok: boolean;
  configured: boolean;
  ms?: number;
  model?: string;
  provider?: string;
  reply?: string;
  error?: string;
}

export const api = {
  // --- Meta ---
  capabilities: () => get<ServerCapabilities>('/capabilities'),
  health: () => get<{ status: string; version: string }>('/health'),
  diagnostics: () => get<Diagnostics>('/diagnostics'),
  /** Days anything at all landed on — see the Log's gap rows. */
  /** Help now moments per day — counts only, for the Log's day tally. */
  sosDays: () => get<{ day: string; n: number }[]>('/insights/sos-days'),
  activeDays: (from?: string) =>
    get<string[]>(`/insights/active-days${from ? `?from=${from}` : ''}`),
  /** Costs a model call. Only ever from a button the person pressed. */
  aiCheck: () => post<AiCheck>('/diagnostics/ai-check'),

  // --- Auth ---
  session: () =>
    get<{ user: PublicUser | null; travel: { zone: string; since: string } | null }>(
      '/auth/session',
    ),
  /** Answering "you seem to have moved" — the only thing that may move a dose time. */
  answerTravel: (answer: 'moved' | 'kept') => post<{ ok: true }>('/auth/travel', { answer }),
  signup: (body: { email: string; password: string; displayName: string; timezone: string }) =>
    post<{ user: PublicUser }>('/auth/signup', body),
  login: (body: { email: string; password: string }) =>
    post<{ user: PublicUser }>('/auth/login', body),
  logout: () => post<{ ok: boolean }>('/auth/logout'),

  // --- Passkeys ---
  passkeyRegisterOptions: () => post<unknown>('/auth/passkey/register/options'),
  passkeyRegisterVerify: (response: unknown, label: string | null) =>
    post<Passkey>('/auth/passkey/register/verify', { response, label }),
  passkeyLoginOptions: () => post<unknown>('/auth/passkey/login/options'),
  passkeyLoginVerify: (response: unknown) =>
    post<{ user: PublicUser }>('/auth/passkey/login/verify', { response }),
  listPasskeys: () => get<Passkey[]>('/auth/passkeys'),
  deletePasskey: (id: string) => del(`/auth/passkeys/${encodeURIComponent(id)}`),

  // --- Mood ---
  todayMood: (day?: string) => get<TodayMood>(`/mood/today${day ? `?day=${day}` : ''}`),
  /**
   * Remove a check-in.
   *
   * The route has existed since check-ins did and nothing in the app could
   * reach it, so a mood logged by accident — or one saved twice from inside a
   * journal entry — was permanent. The server checks ownership and recomputes
   * the day's stats afterwards.
   */
  deleteCheckin: (id: string) => del(`/mood/checkins/${id}`),
  listCheckins: (limit = 100) =>
    get<{ items: MoodCheckin[]; nextCursor: string | null }>(`/mood/checkins?limit=${limit}`),
  /** The three kinds of entry between two instants (ISO), for one day on Days. */
  /** One day's check-ins by the frozen day column, so a 1am check-in filed under the night before is on its own day. */
  listCheckinsOn: (day: string) => get<{ items: MoodCheckin[]; nextCursor: string | null }>(`/mood/checkins?limit=100&day=${day}`),
  listCheckinsBetween: (from: string, to: string) =>
    get<{ items: MoodCheckin[]; nextCursor: string | null }>(`/mood/checkins?limit=100&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  listJournalOn: (day: string) => get<{ items: VoiceLog[]; nextCursor: string | null }>(`/voice-logs?limit=100&day=${day}`),
  listJournalBetween: (from: string, to: string) =>
    get<{ items: VoiceLog[]; nextCursor: string | null }>(`/voice-logs?limit=100&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  createCheckin: (body: CreateMoodCheckinInput) =>
    post<MoodCheckin & { crisis?: CrisisNotice | null }>('/mood/checkins', body),
  /** The slider's revision of a recent check-in: today only. */
  updateCheckin: (id: string, body: UpdateMoodCheckin) =>
    patch<MoodCheckin & { crisis?: CrisisNotice | null }>(`/mood/checkins/${encodeURIComponent(id)}`, body),

  // --- Thought records ---
  distortions: () => get<CognitiveDistortion[]>('/thought-records/distortions'),
  listThoughtRecords: (query = '') =>
    get<{ items: ThoughtRecord[]; nextCursor: string | null }>(`/thought-records${query}`),
  getThoughtRecord: (id: string) => get<ThoughtRecord>(`/thought-records/${id}`),
  createThoughtRecord: (body: CreateThoughtRecord) =>
    post<ThoughtRecord & { crisis?: CrisisNotice | null }>('/thought-records', body),
  updateThoughtRecord: (id: string, body: Partial<CreateThoughtRecord>) =>
    patch<ThoughtRecord & { crisis?: CrisisNotice | null }>(`/thought-records/${id}`, body),
  deleteThoughtRecord: (id: string) => del(`/thought-records/${id}`),

  // --- Support / SOS ---
  crisis: () => get<CrisisInfo>('/support/crisis'),
  tools: (crisisSafeOnly = false) =>
    get<CbtTool[]>(`/support/tools${crisisSafeOnly ? '?crisisSafeOnly=true' : ''}`),
  tool: (key: string) => get<CbtTool>(`/support/tools/${key}`),
  /** The screen's contents, without recording that it was opened. */
  /**
   * The companion, typed.
   *
   * Same route, same safety checks and same model as the spoken version — the
   * crisis check runs on the message before any model call, so it works with no
   * network and no key.
   */
  chatConfig: () => get<{ enabled: boolean; model: string | null; provider: string }>('/chat/config'),
  sendChat: (body: { threadId: string | null; content: string; includeContext?: boolean }) =>
    post<{
      threadId: string;
      message: { id: string; role: 'assistant'; content: string; createdAt: string; crisisFlagged: boolean };
      crisisResources?: { name: string; contact: string }[];
    }>('/chat/messages', body),
  /** Used when a typed conversation is left — see TypeItThrough. */
  deleteChatThread: (id: string) => del(`/chat/threads/${encodeURIComponent(id)}`),

  /** The big ongoing things — see components/DayLifeEvent. */
  lifeEvents: () => get<OwnLifeEvent[]>('/life-events'),
  /** `label` is optional and usually absent: marking is two taps and no keyboard. */
  /** PROTOTYPE — one event and what he wrote while it ran. */
  lifeEventSpan: (id: string) =>
    get<{
      event: OwnLifeEvent;
      items: { kind: 'journal' | 'record' | 'note'; id: string; day: string; at: string; text: string }[];
      days: {
        day: string; checkins: number; entries: number; records: number; sos: number;
        medsTaken: number; medsScheduled: number; meanMood: number | null;
        read: string | null; state: string | null;
      }[];
      before: { meanMood: number | null; days: number };
      spanDays: number;
    }>(`/life-events/${id}/span`),
  /** Day marks — one tap says a thing happened on a day. */
  dayMarks: (from: string, to: string) =>
    get<{ day: string; kind: string; source?: 'you' | 'app' }[]>(`/day-marks?from=${from}&to=${to}`),
  setDayMark: (day: string, kind: string) =>
    post<{ day: string; kind: string; marked: boolean }>('/day-marks', { day, kind }),
  /** Two a day (PRD-2.0 §2). The key is 404 'not configured' on a server without VAPID keys. */
  pushKey: () => get<{ publicKey: string }>('/push/key'),
  pushSubscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    post<{ ok: boolean }>('/push/subscriptions', sub),
  pushUnsubscribe: (endpoint: string) =>
    request<{ ok: boolean }>('/push/subscriptions', { method: 'DELETE', body: JSON.stringify({ endpoint }), headers: { 'Content-Type': 'application/json' } }),
  pushTest: () => post<{ sent: number }>('/push/test'),
  /** Keep a mark the door set: it becomes his and the chip turns solid. */
  keepDayMark: (day: string, kind: string) =>
    post<{ day: string; kind: string; marked: boolean; source: 'you' }>('/day-marks/keep', { day, kind }),
  createLifeEvent: (body: {
    label?: string;
    startedOn: string;
    note?: string | null;
    kind?: 'event' | 'thread';
    countFrom?: string | null;
  }) => post<OwnLifeEvent>('/life-events', body),
  updateLifeEvent: (
    id: string,
    body: {
      label?: string;
      startedOn?: string;
      status?: 'active' | 'ended' | 'dismissed';
      endedOn?: string | null;
      kind?: 'event' | 'thread';
      countFrom?: string | null;
    },
  ) => patch<OwnLifeEvent>(`/life-events/${encodeURIComponent(id)}`, body),

  // --- The door (release 4 of 2.0) — owner-only ---
  doorPropose: (voiceLogId: string) => post<DoorProposalResponse>('/door/propose', { voiceLogId }),
  doorFile: (voiceLogId: string, picks: DoorFileBody['picks']) =>
    post<{ voiceLogId: string; day: string; filed: { id: string; kind: string; ok: boolean; note?: string }[] }>('/door/file', { voiceLogId, picks }),
  doorUnfile: (voiceLogId: string, pick: DoorFileBody['picks'][number]) =>
    post<{ voiceLogId: string; day: string; id: string; kind: string; removed: number }>('/door/unfile', { voiceLogId, pick }),
  doorCorrect: (kind: DoorPickKind, key: string) => post<{ ok: boolean }>('/door/correct', { kind, key }),
  doorCorrections: () => get<{ kind: string; key: string; takenOff: number; updatedAt: string }[]>('/door/corrections'),
  clearDoorCorrections: () => del('/door/corrections'),

  // --- The day's faces (release 3 of 2.0) — owner-only ---
  dayFace: (day: string) => get<DayFace>(`/days/${day}/face`),
  setDaySleep: (day: string, sleep: DaySleep | null) => request<DayFace>(`/days/${day}/sleep`, { method: 'PUT', body: JSON.stringify({ sleep }) }),
  /** Sleep from Health: the Shortcut's key. The key itself is shown once, on making it. */
  shortcutKey: () => get<{ hasKey: boolean; createdAt: string | null; lastUsedAt: string | null }>('/shortcuts/key'),
  makeShortcutKey: () => post<{ key: string }>('/shortcuts/key', {}),
  forgetShortcutKey: () => del('/shortcuts/key'),
  closeDay: (day: string) => post<DayFace>(`/days/${day}/close`),
  reopenDay: (day: string) => request<DayFace>(`/days/${day}/close`, { method: 'DELETE' }),

  // --- People (release 2 of 2.0) — owner-only; nothing here reaches a link ---
  people: (includeHidden = false) => get<Person[]>(`/people${includeHidden ? '?hidden=1' : ''}`),
  createPerson: (name: string) => post<Person>('/people', { name }),
  person: (id: string) => get<Person & { daysList: PersonDay[] }>(`/people/${encodeURIComponent(id)}`),
  updatePerson: (id: string, body: { name?: string; aliases?: string[]; hidden?: boolean }) =>
    patch<Person>(`/people/${encodeURIComponent(id)}`, body),
  mergePerson: (id: string, into: string) =>
    post<Person>(`/people/${encodeURIComponent(id)}/merge`, { into }),
  dayPeople: (day: string) => get<DayPerson[]>(`/days/${day}/people`),
  addDayPerson: (day: string, name: string, voiceLogId: string | null) =>
    post<DayPerson[]>(`/days/${day}/people`, { name, voiceLogId }),
  removeDayPerson: (day: string, personId: string) =>
    request<DayPerson[]>(`/days/${day}/people/${encodeURIComponent(personId)}`, { method: 'DELETE' }),
  deleteLifeEvent: (id: string) => del(`/life-events/${encodeURIComponent(id)}`),
  /** "Yes, still going" — answers the reminder without editing anything else. */
  lifeEventChecked: (id: string) =>
    post<{ ok: true }>(`/life-events/${encodeURIComponent(id)}/checked`, {}),
  /**
   * Answering the offer raised by an entry.
   *
   * No label is sent because none was offered: the model said that something is
   * going on, not what. Naming is a separate step afterwards, if it ever comes.
   */
  answerLifeEventOffer: (offerId: string, body: { accepted: boolean; startedOn?: string }) =>
    post<{ answered: boolean; lifeEventId: string | null; lifeEvent: OwnLifeEvent | null }>(
      `/life-event-offers/${encodeURIComponent(offerId)}/answer`,
      body,
    ),

  /**
   * Weekly recaps.
   *
   * These endpoints have existed since the report was built and no screen ever
   * called them, so the only place a recap could be read was a share link —
   * a clinician could see the owner's week and the owner could not.
   */
  weeklyReports: () => get<WeeklyReport[]>('/insights/reports'),
  /*
   * Ask again for one week. The list route writes a week only where NO row
   * exists, deliberately — that is what stops an empty or refused week being
   * paid for on every visit — so a week that failed would otherwise keep its
   * failure for ever. This is the way back, and it is a button rather than a
   * retry loop because report generation is the most expensive call in the app.
   */
  writeWeek: (weekStart: string) =>
    post<WeeklyReport>('/insights/reports', { weekStart }),
  /*
   * One week, by its Monday. The route has existed since the report was built
   * and nothing has ever called it — the whole table was reachable only through
   * the list, so a clinician with a share link could read a week the owner
   * could not open on its own.
   *
   * Not gated on the AI rung, deliberately: the rung governs what gets MADE,
   * and a recap already written stays readable.
   */
  weekReport: (weekStart: string) =>
    get<WeeklyReport>(`/insights/reports/${encodeURIComponent(weekStart)}`),
  /** The month, for the owner (release 5b): figures counted from the record, and the account once the month is over. */
  month: (month: string, grid = false) => get<MonthPage>(`/insights/months/${encodeURIComponent(month)}${grid ? '?grid=1' : ''}`),
  months: () => get<{ months: string[] }>('/insights/months'),
  /**
   * How this week reads on a partner-style link (How I am today without the
   * figure ticks), and the owner's switch to leave it off links. Null when no
   * reading was ever made for the week.
   */
  weekReadings: (weekStart: string) =>
    get<{ seemed: WeekReading | null; held: WeekReading | null }>(
      `/insights/reports/${encodeURIComponent(weekStart)}/readings`,
    ),
  setWeekReadingWithdrawn: (weekStart: string, kind: WeekReading['kind'], withdrawn: boolean) =>
    patch<{ reading: WeekReading | null }>(
      `/insights/reports/${encodeURIComponent(weekStart)}/readings/${kind}`,
      { withdrawn },
    ),
  /** Defaults to the week that just finished; a report on a live week misleads. */
  generateWeeklyReport: (body: { weekStart?: string; regenerate?: boolean } = {}) =>
    post<WeeklyReport>('/insights/reports', body),

  /** The last N days at a glance, for the recap strip. */
  dayGlances: (days = 7): Promise<DayGlance[]> =>
    get<DayGlance[]>(`/insights/days?days=${days}`),
  /** How a day went, for the person whose day it was. */
  /**
   * `defer` returns the counted figures at once, with the sentence reported as
   * pending. Ask for that first, render the day, then ask again without it —
   * the second call is what actually commissions the writing.
   */
  daySummary: (day: string, defer = false): Promise<SharedDailyStatus> =>
    get<SharedDailyStatus>(
      `/insights/day/${encodeURIComponent(day)}${defer ? '?defer=1' : ''}`,
    ),
  sosTools: (): Promise<SosStart> => get<SosStart>('/support/sos/tools'),
  startSos: (body: { distressBefore?: number | null; trigger?: string | null }) =>
    post<SosStart>('/support/sos', body),
  closeSos: (
    id: string,
    /* Hand-written rather than taken from `closeSos` in @adhd/shared, which is
       how `distressBeforeRecalled` could be added to the schema and the route
       and still not compile at the one place that sends it. */
    body: {
      pathTaken?: string | null;
      distressAfter?: number | null;
      distressBeforeRecalled?: number | null;
      note?: string | null;
    },
  ) => post<{ ok: boolean }>(`/support/sos/${id}/close`, body),
  logToolSession: (body: {
    toolKey: string;
    payload?: Record<string, unknown>;
    distressBefore?: number | null;
    distressAfter?: number | null;
    helpfulness?: number | null;
    completedAt?: string | null;
  }) => post<{ id: string }>('/support/tools/sessions', body),
  quote: (at?: 'night') => get<DailyQuote | null>(`/support/quote${at ? '?at=night' : ''}`),

  // --- Journal ---
  listJournal: (limit = 50) =>
    get<{ items: VoiceLog[]; nextCursor: string | null }>(`/voice-logs?limit=${limit}`),
  getJournalEntry: (id: string) => get<VoiceLog>(`/voice-logs/${id}`),
  createTextEntry: (body: { text: string; kind?: string; title?: string; day?: string }) =>
    post<VoiceLog & { crisis?: CrisisNotice | null }>('/voice-logs/text', body),
  uploadRecording: (blob: Blob, meta: { kind?: string; durationSeconds?: number; day?: string }) => {
    const form = new FormData();
    // The fields go BEFORE the audio. The server now reads every part whichever
    // side of the file it falls, but it used to read only the ones in front of
    // it, and a recording is megabytes: `day` and `kind` sent after it were
    // lost, so a journal meant for yesterday was filed under today (13 Sep).
    if (meta.kind) form.append('kind', meta.kind);
    if (meta.durationSeconds !== undefined) {
      form.append('durationSeconds', String(Math.round(meta.durationSeconds)));
    }
    // Only ever sent inside the late-night grace, and only when he picked
    // yesterday. The server accepts today or yesterday-before-4am and nothing
    // else, so this cannot become a date picker by accident.
    if (meta.day) form.append('day', meta.day);
    // Filename matters only as a hint; the server derives storage paths from a
    // UUID and never trusts anything the client sends here.
    form.append('audio', blob, 'entry.webm');
    return request<VoiceLog>('/voice-logs', { method: 'POST', body: form });
  },
  retranscribe: (id: string) => post<{ status: string }>(`/voice-logs/${id}/transcribe`),

  /**
   * Speech to text with nothing kept — no journal entry, no stored audio.
   *
   * For answering a question inside a thought record. Going through
   * `createVoiceLog` would file each answer in the journal as an entry of its
   * own, and a five-step record would leave five fragments nobody meant to
   * write.
   */
  /**
   * A short-lived key for a browser-to-OpenAI voice conversation.
   *
   * The real API key never leaves the server; this one expires in minutes and is
   * scoped to a single session whose instructions the server wrote.
   */
  realtimeSession: (formatKey: string, mode: 'record' | 'support' = 'record') =>
    post<{ value: string; expiresAt: number; model: string }>('/voice/realtime-session', {
      formatKey,
      mode,
    }),

  /**
   * The app's own crisis check, over something just said aloud.
   *
   * A realtime conversation never touches our server, so the model's judgement
   * would otherwise be the only safeguard on it — and in testing that judgement
   * failed, replying to "everyone would be better off if I wasn't here" with a
   * warm follow-up question. This is lexical, deterministic, and cannot be
   * talked out of firing.
   */
  /**
   * The AI account in force. Never carries a key — see the route; the client is
   * told whether one is set and nothing more.
   */
  aiKeys: () =>
    get<{
      openai: { set: boolean; own: boolean };
      anthropic: { set: boolean; own: boolean };
      reasoning: 'anthropic' | 'openai' | 'none';
      transcription: boolean;
      canStore: boolean;
    }>('/auth/keys'),
  setAiKey: (provider: 'openai' | 'anthropic', key: string) =>
    post<{ ok: true }>('/auth/keys', { provider, key }),
  clearAiKey: (provider: 'openai' | 'anthropic') =>
    del(`/auth/keys/${encodeURIComponent(provider)}`),

  updateProfile: (body: {
    displayName?: string;
    timezone?: string;
    aiReview?: 'off' | 'auto' | 'button';
    aiLevel?: 'off' | 'transcribe' | 'summarise' | 'reflect' | 'converse';
    lifeEventReminder?: boolean;
    aiWarmth?: 'plain' | 'balanced' | 'warm'; morningEndsAt?: string; nightStartsAt?: string; doorFiling?: boolean; ownWordsBack?: boolean; doorAsks?: boolean; sleepFromHealth?: boolean; shortNightUnder?: number; longNightOver?: number;
    hideMarksAtRest?: boolean;
    notifications?: boolean;
    morningAt?: string; }) =>
    patch<{ user: PublicUser }>('/auth/profile', body),

  safetyCheck: (text: string) =>
    post<{ level: 'none' | 'elevated' | 'high'; resources: { name: string; contact: string; description: string; available: string }[] }>(
      '/voice/safety-check',
      { text },
    ),

  transcribeOnce: (blob: Blob) => {
    const form = new FormData();
    form.append('audio', blob, 'answer.webm');
    return request<{ text: string }>('/voice/transcribe', { method: 'POST', body: form });
  },
  /** The whole list as it should now read; the server narrows the app's mark to what is left. */
  updateJournalTags: (id: string, themes: string[]) =>
    patch<VoiceLog>(`/voice-logs/${id}/tags`, { themes }),
  deleteJournalEntry: (id: string) => del(`/voice-logs/${id}`),
  /** Turn a transcript into a reviewable thought-record draft. Never auto-saved. */
  draftFromTranscript: (transcript: string, voiceLogId?: string) =>
    post<{ draft: ThoughtRecordDraft | null; error: string | null }>('/thought-records/draft', {
      transcript,
      ...(voiceLogId ? { voiceLogId } : {}),
    }),

  // --- Insights ---
  streak: () => get<StreakState>('/insights/streak'),

  // --- Medications ---
  /** Picker data only. It names drugs; it does not advise on any of them. */
  medicationReference: () => get<MedicationReference[]>('/medications/reference'),
  /**
   * Stopped medications are left out unless asked for. The flag is omitted
   * rather than sent as `false`, because the server coerces this query
   * parameter to a boolean and every non-empty string — `"false"` included —
   * coerces to true.
   */
  listMedications: (includeInactive = false) =>
    get<Medication[]>(`/medications${includeInactive ? '?includeInactive=true' : ''}`),
  createMedication: (body: CreateMedication) => post<Medication>('/medications', body),
  updateMedication: (id: string, body: UpdateMedication) =>
    patch<Medication>(`/medications/${encodeURIComponent(id)}`, body),
  /** Takes the dose history with it — the server cascades, and nothing is kept. */
  deleteMedication: (id: string) => del(`/medications/${encodeURIComponent(id)}`),
  medicationsToday: () => get<MedicationsToday>('/medications/today'),
  /** One past day's doses, so a dose noticed days later can still be corrected. */
  medicationsOnDay: (day: string) =>
    get<MedicationsToday>(`/medications/day/${encodeURIComponent(day)}`),
  /**
   * Logging the same slot again updates it in place, so a mistap can be
   * corrected by tapping the right thing. Doses with no `scheduledFor` are
   * exempt from that and stack up, which is what an as-needed dose should do.
   */
  logDose: (body: LogDose) => post<{ ok: boolean; day: string }>('/medications/doses', body),
  adherence: (query = '') => get<AdherenceSummary>(`/medications/adherence${query}`),
  /**
   * What the check-ins recorded either side of the date a medication started.
   *
   * This describes what was written down. It is not evidence the medication did
   * it, and nothing rendering it may say otherwise: starting treatment tends to
   * coincide with a diagnosis, a change in circumstances and simply deciding to
   * act, titration means the early weeks are not the working dose, and people
   * usually start at their worst, which regression to the mean flatters. So the
   * caller checks `hasEnoughData` before drawing anything, and keeps those
   * caveats beside the numbers rather than below them — on screen they are the
   * only thing between a small sample and a dose changed without a prescriber.
   */
  medicationEffect: (id: string, windowDays = 30) =>
    get<MedicationEffect>(`/medications/${encodeURIComponent(id)}/effect?windowDays=${windowDays}`),

  // --- Sharing ---
  listShares: () => get<Share[]>('/shares'),
  /**
   * The only response that ever carries the link. The server keeps a hash, so
   * if this one is lost the share can be revoked but never re-read.
   *
   * A 409 whose code is 'confirmation_required' carries `details.needed`: the
   * confirmations still missing, which the sheet then asks for.
   */
  createShare: (body: CreateShareInput) => post<Share & { url: string }>('/shares', body),
  /** Change what a link shows, in place — the same URL. The same 409 as createShare. */
  changeShareLevel: (id: string, body: ChangeShareLevel) =>
    patch<Share>(`/shares/${encodeURIComponent(id)}/level`, body),
  /** "See what they see": the link's page as it is now. Not recorded as an opening. */
  /*
   * Therapy homework: a question he was set, for a while, answered privately.
   *
   * Nothing here is shareable and there is no grant for it — see migration 034.
   * That is why it has its own routes rather than riding on the journal: the
   * shared surface must not be a function of the homework, and the only way to
   * be sure of that is for the sharing path never to read it at all.
   */
  homework: () => get<{ today: string; items: Homework[] }>('/homework'),
  homeworkOne: (id: string) =>
    get<{ homework: Homework; answers: HomeworkAnswer[] }>(`/homework/${encodeURIComponent(id)}`),
  startHomework: (body: { title: string; fields: HomeworkField[]; endsOn: string }) =>
    post<Homework>('/homework', body),
  /** A draft he then edits. Writes nothing — only startHomework does. */
  composeHomework: (body: { said: string }) =>
    post<{ title: string; fields: HomeworkField[]; endsOn: string }>('/homework/compose', body),
  answerHomework: (id: string, body: { values: Record<string, HomeworkValue> }) =>
    post<HomeworkAnswer>(`/homework/${encodeURIComponent(id)}/answers`, body),
  updateHomework: (id: string, body: { title?: string; fields?: HomeworkField[]; endsOn?: string }) =>
    patch<Homework>(`/homework/${encodeURIComponent(id)}`, body),
  /** Made for the appointment, read once, never stored. */
  readBackHomework: (id: string) =>
    post<{ text: string }>(`/homework/${encodeURIComponent(id)}/readback`, {}),
  stopHomework: (id: string) => post<Homework>(`/homework/${encodeURIComponent(id)}/stop`, {}),

  sharePreview: (id: string) => get<SharedView>(`/shares/${encodeURIComponent(id)}/preview`),
  /**
   * The link again, for the owner. Asked for on a tap rather than carried on
   * the share list, so a working credential is not sitting in every response
   * the Sharing screen fetches.
   */
  shareUrl: (id: string) => get<{ url: string }>(`/shares/${encodeURIComponent(id)}/link`),
  /** Circle (release 5 of 2.0): the per-link settings that are not grants. */
  updateShareCircle: (id: string, body: { whatHelps?: string | null; whatHelpsOn?: boolean; wavesOn?: boolean; holderNames?: string[]; role?: 'therapist' | 'partner' | 'friend' | null; sessionOn?: string | null; note?: string | null; noteOn?: boolean }) =>
    patch<Share>(`/shares/${encodeURIComponent(id)}/circle`, body),
  /** The waves of the last two days: who tapped "thinking of you", and when. Never a count. */
  waves: () => get<Wave[]>('/waves'),
  /** The one thing a recipient can do besides read. Public; one row. */
  wave: (token: string) => request<{ ok: boolean }>(`/shared/${encodeURIComponent(token)}/wave`, { method: 'POST' }),
  revokeShare: (id: string) => post<{ ok: boolean }>(`/shares/${encodeURIComponent(id)}/revoke`),
  deleteShare: (id: string) => del(`/shares/${encodeURIComponent(id)}`),
  shareAccessLog: (id: string) =>
    get<ShareAccessLogEntry[]>(`/shares/${encodeURIComponent(id)}/access-log`),
  /** Called with no session, by whoever was given the link. */
  /** `doc` names the document the page is being read as, so the owner's access log says so. Same payload. */
  sharedView: (token: string, doc?: 'brief' | 'week' | 'card') =>
    get<SharedView>(`${PUBLIC_SHARE_PATH}${encodeURIComponent(token)}${doc ? `?doc=${doc}` : ''}`),
};
